<template>
  <div>
    <div class="border-bottom bg-white p-3">
      <div class="d-flex align-items-center gap-3">
        <button
          type="button"
          class="btn btn-link p-0 text-decoration-none"
          @click="$emit('cancel')"
        >
          <i class="bi bi-arrow-left"></i> Back
        </button>
        <h5 class="mb-0 fw-bold">Editing Report</h5>
      </div>
    </div>

    <div class="bg-white p-3">
      <form @submit.prevent="handleSave">
        <div v-for="field in fieldOrder" :key="field" class="mb-4">
          <label
            :for="`field-${field}`"
            class="form-label text-uppercase text-muted small fw-semibold"
          >
            {{ fieldLabel(field) }}
          </label>

          <!-- Special handling for array field -->
          <div v-if="field === arrayFieldName">
            <div
              v-for="(item, index) in formData[field]"
              :key="index"
              class="mb-2 d-flex gap-2"
            >
              <input
                type="text"
                :id="`field-${field}-${index}`"
                class="form-control"
                v-model="formData[field][index]"
                :placeholder="`Feature ${index + 1}`"
              />
              <button
                v-if="index >= MINIMUM_REQUIRED_FEATURES"
                type="button"
                class="btn btn-outline-danger"
                @click="removeArrayItem(field, index)"
              >
                <i class="bi bi-trash"></i>
              </button>
            </div>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              @click="addArrayItem(field)"
            >
              <i class="bi bi-plus"></i> Add item
            </button>
          </div>

          <!-- Textarea for longer text fields -->
          <textarea
            v-else-if="isLongTextField(field)"
            :id="`field-${field}`"
            class="form-control"
            v-model="formData[field]"
            rows="4"
            :placeholder="`Enter ${field.toLowerCase()}`"
          ></textarea>

          <!-- Regular text input for other fields -->
          <input
            v-else
            type="text"
            :id="`field-${field}`"
            class="form-control"
            v-model="formData[field]"
            :placeholder="`Enter ${field.toLowerCase()}`"
          />
        </div>

        <div class="d-flex gap-2 mt-4 pt-3 border-top">
          <button type="submit" class="btn btn-primary">Save</button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="$emit('cancel')"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { FactSheetFields } from "@/types/index.ts";
import {
  DEFAULT_NATIONAL_INDIGENOUS_FEATURES,
  ISLAND_DISPLAY_CONFIG,
  MINIMUM_REQUIRED_FEATURES
} from "@/helpers/displayConfig.js";

export default {
  name: "ReportEditForm",
  props: {
    report: {
      type: Object,
      default: null
    },
    fieldOrder: {
      type: Array,
      default: () => []
    }
  },
  emits: ["save", "cancel"],
  data() {
    return {
      formData: {},
      arrayFieldName:
        FactSheetFields.NATIONAL_AND_OR_INDIGENOUS_FEATURES_AND_CHARACTERISTICS,
      MINIMUM_REQUIRED_FEATURES
    };
  },
  computed: {
    longTextFields() {
      return [
        FactSheetFields.POLITICAL_STRUCTURE,
        FactSheetFields.NATIONAL_AND_OR_INDIGENOUS_FEATURES_AND_CHARACTERISTICS
      ];
    }
  },
  watch: {
    report: {
      immediate: true,
      deep: true,
      handler(newReport) {
        this.initializeFormData(newReport);
      }
    }
  },
  methods: {
    fieldLabel(field) {
      return (
        ISLAND_DISPLAY_CONFIG.find((config) => config.field === field)?.label ||
        field
      );
    },
    initializeFormData(report) {
      if (!report || !report.reportDetails) {
        // Initialize empty form data
        this.formData = {};
        this.fieldOrder.forEach((field) => {
          if (field === this.arrayFieldName) {
            this.formData[field] = [...DEFAULT_NATIONAL_INDIGENOUS_FEATURES];
          } else {
            this.formData[field] = "";
          }
        });
        return;
      }

      // Pre-populate form data from report
      this.formData = {};
      this.fieldOrder.forEach((field) => {
        if (field === this.arrayFieldName) {
          // Handle array field
          const value = report.reportDetails[field];
          if (Array.isArray(value) && value.length > 0) {
            // Ensure we always have at least 3 items (the default minimum)
            const paddedValue = [...value];
            while (paddedValue.length < MINIMUM_REQUIRED_FEATURES) {
              paddedValue.push("");
            }
            this.formData[field] = paddedValue;
          } else {
            this.formData[field] = [...DEFAULT_NATIONAL_INDIGENOUS_FEATURES];
          }
        } else {
          // Handle regular fields
          this.formData[field] = report.reportDetails[field] || "";
        }
      });
    },
    isLongTextField(field) {
      return (
        this.longTextFields.includes(field) && field !== this.arrayFieldName
      );
    },
    addArrayItem(field) {
      if (!this.formData[field]) {
        this.formData[field] = [];
      }
      this.formData[field].push("");
    },
    removeArrayItem(field, index) {
      // Prevent removal of the first 3 default fields (indices 0, 1, 2)
      // Only allow removing items beyond the first 3 (index 3 and above)
      if (
        this.formData[field] &&
        index >= MINIMUM_REQUIRED_FEATURES &&
        this.formData[field].length > MINIMUM_REQUIRED_FEATURES
      ) {
        this.formData[field].splice(index, 1);
      }
    },
    handleSave() {
      // Clean up empty array items before saving
      const cleanedData = { ...this.formData };
      if (cleanedData[this.arrayFieldName]) {
        // Always preserve the first 3 items (even if empty)
        const firstThree = cleanedData[this.arrayFieldName].slice(
          0,
          MINIMUM_REQUIRED_FEATURES
        );
        const additionalItems = cleanedData[this.arrayFieldName].slice(
          MINIMUM_REQUIRED_FEATURES
        );

        // Filter out empty items from additional items only
        const cleanedAdditional = additionalItems.filter(
          (item) => item && item.toString().trim().length > 0
        );

        // Combine first 3 (preserved) with cleaned additional items
        cleanedData[this.arrayFieldName] = [
          ...firstThree,
          ...cleanedAdditional
        ];
      }

      // Remove empty string values for non-array fields
      Object.keys(cleanedData).forEach((key) => {
        if (key !== this.arrayFieldName && cleanedData[key] === "") {
          delete cleanedData[key];
        }
      });

      this.$emit("save", cleanedData);
    }
  }
};
</script>

<style scoped>
/* All styling handled with Bootstrap utility classes */
</style>
